// DataSourceModal.js - Modal for changing data sources for component parameters

import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import THEME from '../../styles/theme';
import useStore from '../../store/useStore';
import { getAvailableDataSources, getCurrentDataSource } from '../../utils/dataSourceHelpers';

const DataSourceModal = ({ node, onClose, onApply }) => {
  const { componentLibrary } = useStore();

  // Parameters that support data sources
  const dataSourceParams = ['gain', 'noiseFigure', 'p1db', 'oip3'];

  // Build initial state from current sources
  const initialSources = {};
  dataSourceParams.forEach(paramKey => {
    const availableSources = getAvailableDataSources(node, paramKey, componentLibrary);
    if (availableSources.length > 1) {
      initialSources[paramKey] = getCurrentDataSource(node, paramKey);
    }
  });

  const [selectedSources, setSelectedSources] = useState(initialSources);

  // Draggable modal state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);

  const handleSourceChange = (paramKey, sourceValue) => {
    setSelectedSources(prev => ({
      ...prev,
      [paramKey]: sourceValue
    }));
  };

  const handleApply = () => {
    onApply(selectedSources);
    onClose();
  };

  // Drag handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners when dragging
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  // Get display name for parameter
  const getParamDisplayName = (paramKey) => {
    const param = node.data.parameters[paramKey];
    return param?.name || paramKey;
  };

  // Source icons
  const sourceIcons = {
    's2p': '𝐒',
    'database': '📊',
    'manual': '✋'
  };

  // Check if there are any parameters with multiple sources
  const hasMultipleSources = Object.keys(initialSources).length > 0;

  if (!hasMultipleSources) {
    return null;
  }

  return (
    <Backdrop onClick={onClose}>
      <Modal
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
      >
        <Header onMouseDown={handleMouseDown}>
          <Title>Change Data Sources</Title>
          <ComponentName>{node.data.alias || node.data.partNumber || node.data.name}</ComponentName>
        </Header>

        <Content>
          {dataSourceParams.map(paramKey => {
            const availableSources = getAvailableDataSources(node, paramKey, componentLibrary);
            if (availableSources.length <= 1) return null;

            return (
              <ParamRow key={paramKey}>
                <ParamLabel>{getParamDisplayName(paramKey)}:</ParamLabel>
                <SourceSelect
                  value={selectedSources[paramKey]}
                  onChange={(e) => handleSourceChange(paramKey, e.target.value)}
                >
                  {availableSources.map(source => (
                    <option key={source.value} value={source.value}>
                      {sourceIcons[source.value]} {source.label}
                    </option>
                  ))}
                </SourceSelect>
              </ParamRow>
            );
          })}
        </Content>

        <Footer>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <ApplyButton onClick={handleApply}>Apply</ApplyButton>
        </Footer>
      </Modal>
    </Backdrop>
  );
};

// Styled Components
const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${THEME.zIndex.modal + 10};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Modal = styled.div`
  background: ${THEME.sidebarBackground};
  border: 1px solid ${THEME.borderColor};
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
  width: 320px;
  max-width: 90vw;
  z-index: ${THEME.zIndex.modal + 11};
`;

const Header = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${THEME.borderColor};
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const Title = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: ${THEME.textColor};
`;

const ComponentName = styled.div`
  font-size: 14px;
  color: ${THEME.textSecondary};
  font-weight: 500;
`;

const Content = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ParamRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ParamLabel = styled.label`
  flex: 0 0 60px;
  font-size: 14px;
  font-weight: 500;
  color: ${THEME.textColor};
`;

const SourceSelect = styled.select`
  flex: 1;
  padding: 8px 12px;
  background: ${THEME.inputBackground};
  color: ${THEME.textColor};
  border: 1px solid ${THEME.borderColor};
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  text-align-last: center;

  &:hover {
    border-color: ${THEME.accentPrimary};
  }

  &:focus {
    outline: none;
    border-color: ${THEME.accentPrimary};
    box-shadow: 0 0 0 3px rgba(220, 20, 60, 0.1);
  }

  option {
    background: ${THEME.sidebarBackground};
    color: ${THEME.textColor};
  }
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${THEME.borderColor};
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
`;

const CancelButton = styled(Button)`
  background: transparent;
  color: ${THEME.textSecondary};

  &:hover {
    background: ${THEME.hoverBackground};
    color: ${THEME.textColor};
  }
`;

const ApplyButton = styled(Button)`
  background: ${THEME.accentPrimary};
  color: white;

  &:hover {
    background: #b8163e;
  }
`;

export default DataSourceModal;
